import { useState } from 'react';
import { UserProgress } from '../services/storage';
import { storageService } from '../services/storage';
import AccentSelector from '../components/pronunciation/AccentSelector';

interface ProfilePageProps {
  user: UserProgress;
  onNavigate?: (page: string) => void;
}

export default function ProfilePage({ user, onNavigate }: ProfilePageProps) {
  const [showExport, setShowExport] = useState(false);
  const [showDelete, setShowDelete] = useState(false);

  const learningRecords = storageService.getUserLearningRecords(user.id);
  const totalPracticeTime = learningRecords.reduce((sum, r) => sum + r.duration, 0);
  const averageScore = learningRecords.length > 0
    ? Math.round(learningRecords.reduce((sum, r) => sum + r.score, 0) / learningRecords.length)
    : 0;

  const handleExport = () => {
    const data = storageService.exportData();
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `english-tutor-backup-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setShowExport(false);
    alert('数据已导出！');
  };

  const handleDeleteUser = () => {
    if (confirm('确定要删除这个账号吗？所有数据将无法恢复！')) {
      storageService.deleteUser(user.id);
      window.location.reload();
    }
    setShowDelete(false);
  };

  const badges = [
    { id: 'starter', name: '新手上路', icon: '🌟', description: '完成第一次练习' },
    { id: '7day_streak', name: '坚持7天', icon: '📚', description: '连续学习7天' },
    { id: '50_words', name: '词汇达人', icon: '📝', description: '掌握50个单词' },
    { id: '100_words', name: '词汇大师', icon: '🎯', description: '掌握100个单词' },
    { id: 'speaker', name: '小演说家', icon: '🎤', description: '口语练习50次' },
    { id: 'listener', name: '听力高手', icon: '🎧', description: '听力练习50次' },
    { id: 'perfect', name: '完美发音', icon: '💯', description: '获得100分' },
    { id: 'level5', name: '进阶达人', icon: '🏆', description: '达到5级' },
    { id: 'level10', name: '英语大师', icon: '👑', description: '达到10级' },
  ];

  return (
    <div className="space-y-8 animate-slide-up">
      {/* Profile header */}
      <div className="bg-gradient-to-r from-indigo-500 to-purple-500 rounded-3xl p-8 text-white shadow-xl">
        <div className="flex items-center gap-6">
          <div className="text-8xl">{user.avatar}</div>
          <div className="flex-1">
            <h1 className="text-3xl font-bold mb-2">{user.name}</h1>
            <p className="text-white/80 mb-4">{user.age}岁</p>
            <div className="flex gap-4">
              <div className="bg-white/20 rounded-2xl px-4 py-2">
                <p className="text-sm text-white/60">等级</p>
                <p className="text-2xl font-bold">Lv.{user.level}</p>
              </div>
              <div className="bg-white/20 rounded-2xl px-4 py-2">
                <p className="text-sm text-white/60">积分</p>
                <p className="text-2xl font-bold">{user.points}</p>
              </div>
              <div className="bg-white/20 rounded-2xl px-4 py-2">
                <p className="text-sm text-white/60">连续</p>
                <p className="text-2xl font-bold">{user.streakDays}天</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          icon="📚"
          label="掌握单词"
          value={user.wordsLearned.length}
        />
        <StatCard
          icon="⏱️"
          label="学习时长"
          value={`${Math.round(totalPracticeTime / 60)}分钟`}
        />
        <StatCard
          icon="📊"
          label="平均分数"
          value={`${averageScore}分`}
        />
        <StatCard
          icon="🏆"
          label="获得徽章"
          value={user.badges.length}
        />
      </div>

      {/* Quick links */}
      <div className="bg-white rounded-3xl p-6 shadow-soft">
        <h2 className="font-bold text-xl text-gray-800 mb-4">更多功能</h2>
        <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
          <button
            onClick={() => onNavigate?.('phrases')}
            className="bg-blue-50 rounded-xl p-3 text-center hover:bg-blue-100 transition-all"
          >
            <span className="text-2xl block mb-1">💬</span>
            <p className="text-xs font-medium text-gray-700">短语</p>
          </button>
          <button
            onClick={() => onNavigate?.('phonetics')}
            className="bg-purple-50 rounded-xl p-3 text-center hover:bg-purple-100 transition-all"
          >
            <span className="text-2xl block mb-1">📖</span>
            <p className="text-xs font-medium text-gray-700">音标</p>
          </button>
          <button
            onClick={() => onNavigate?.('daily-task')}
            className="bg-yellow-50 rounded-xl p-3 text-center hover:bg-yellow-100 transition-all"
          >
            <span className="text-2xl block mb-1">📅</span>
            <p className="text-xs font-medium text-gray-700">打卡</p>
          </button>
          <button
            onClick={() => onNavigate?.('mistake-review')}
            className="bg-red-50 rounded-xl p-3 text-center hover:bg-red-100 transition-all"
          >
            <span className="text-2xl block mb-1">📝</span>
            <p className="text-xs font-medium text-gray-700">错题</p>
          </button>
          <button
            onClick={() => onNavigate?.('virtual-pet')}
            className="bg-pink-50 rounded-xl p-3 text-center hover:bg-pink-100 transition-all"
          >
            <span className="text-2xl block mb-1">🐾</span>
            <p className="text-xs font-medium text-gray-700">宠物</p>
          </button>
          <button
            onClick={() => onNavigate?.('games')}
            className="bg-green-50 rounded-xl p-3 text-center hover:bg-green-100 transition-all"
          >
            <span className="text-2xl block mb-1">🎮</span>
            <p className="text-xs font-medium text-gray-700">游戏</p>
          </button>
        </div>
      </div>

      {/* Badges */}
      <div className="bg-white rounded-3xl p-6 shadow-soft">
        <h2 className="font-bold text-xl text-gray-800 mb-4">我的徽章</h2>
        <div className="grid grid-cols-3 md:grid-cols-5 gap-4">
          {badges.map(badge => {
            const isEarned = user.badges.includes(badge.id);
            return (
              <div
                key={badge.id}
                className={`text-center p-4 rounded-2xl ${
                  isEarned ? 'bg-yellow-50 border-2 border-yellow-400' : 'bg-gray-50 opacity-40'
                }`}
              >
                <div className="text-4xl mb-2">{badge.icon}</div>
                <p className="font-bold text-sm text-gray-700">{badge.name}</p>
                <p className="text-xs text-gray-500 mt-1">{badge.description}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Recent activity */}
      <div className="bg-white rounded-3xl p-6 shadow-soft">
        <h2 className="font-bold text-xl text-gray-800 mb-4">最近学习记录</h2>
        {learningRecords.length === 0 ? (
          <p className="text-center text-gray-400 py-8">还没有学习记录</p>
        ) : (
          <div className="space-y-2">
            {learningRecords.slice(-10).reverse().map(record => (
              <div key={record.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">
                    {record.type === 'vocabulary' ? '📚' :
                     record.type === 'listening' ? '🎧' :
                     record.type === 'speaking' ? '🎤' : '📝'}
                  </span>
                  <div>
                    <p className="font-medium text-gray-800">{record.type}</p>
                    <p className="text-xs text-gray-500">
                      {new Date(record.createdAt).toLocaleString('zh-CN')}
                    </p>
                  </div>
                </div>
                <span className={`font-bold ${
                  record.score >= 80 ? 'text-green-600' :
                  record.score >= 60 ? 'text-blue-600' : 'text-orange-600'
                }`}>
                  {record.score}分
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Settings */}
      <div className="bg-white rounded-3xl p-6 shadow-soft">
        <h2 className="font-bold text-xl text-gray-800 mb-4">设置</h2>

        {/* Accent selector */}
        <div className="mb-4">
          <AccentSelector />
        </div>

        <div className="space-y-3">
          <button
            onClick={() => setShowExport(true)}
            className="w-full p-4 bg-blue-50 text-blue-700 rounded-2xl font-medium hover:bg-blue-100 transition-colors flex items-center gap-3"
          >
            <span className="text-2xl">💾</span>
            <span>导出学习数据</span>
          </button>

          <button
            onClick={() => setShowDelete(true)}
            className="w-full p-4 bg-red-50 text-red-700 rounded-2xl font-medium hover:bg-red-100 transition-colors flex items-center gap-3"
          >
            <span className="text-2xl">🗑️</span>
            <span>删除账号</span>
          </button>
        </div>
      </div>

      {/* About */}
      <div className="bg-gradient-to-r from-yellow-100 to-orange-100 rounded-3xl p-6 text-center">
        <p className="text-2xl mb-2">💜</p>
        <p className="font-bold text-gray-700 mb-2">英语小助手</p>
        <p className="text-sm text-gray-500 mb-2">完全免费的英语学习应用</p>
        <p className="text-xs text-gray-400">使用浏览器内置语音功能，零成本学习</p>
      </div>

      {/* Export modal */}
      {showExport && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full animate-bounce-in">
            <h3 className="font-bold text-xl text-gray-800 mb-4">导出数据</h3>
            <p className="text-gray-600 mb-6">
              将导出所有学习数据，包括用户信息、学习记录等。可用于备份或迁移到其他设备。
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowExport(false)}
                className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-2xl font-medium"
              >
                取消
              </button>
              <button
                onClick={handleExport}
                className="flex-1 py-3 bg-blue-500 text-white rounded-2xl font-bold"
              >
                导出
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete modal */}
      {showDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full animate-bounce-in">
            <h3 className="font-bold text-xl text-red-600 mb-4">⚠️ 删除账号</h3>
            <p className="text-gray-600 mb-6">
              此操作将删除所有数据，无法恢复！确定要继续吗？
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDelete(false)}
                className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-2xl font-medium"
              >
                取消
              </button>
              <button
                onClick={handleDeleteUser}
                className="flex-1 py-3 bg-red-500 text-white rounded-2xl font-bold"
              >
                确认删除
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ icon, label, value }: { icon: string; label: string; value: string | number }) {
  return (
    <div className="bg-white rounded-2xl p-4 shadow-soft text-center">
      <span className="text-3xl mb-2 block">{icon}</span>
      <p className="text-2xl font-bold text-gray-800">{value}</p>
      <p className="text-sm text-gray-500">{label}</p>
    </div>
  );
}